import { useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router";

import { returnPaginationInformation } from "../../helpers/returnPaginationInformation";
import { twMerge } from "../../helpers/twMerge";
import {
	getNumberOfCompaniesToRenderCount,
	getVertical,
} from "../../selectors";
import { sortedCompaniesSelector } from "../../selectors/companies";
import { Pagination } from "../CompanyList/Pagination";
import { NoResults } from "../general/NoResults";
import { BaseCompanies } from "./BaseCompanies";
import { BaseFeaturedCompany } from "./BaseFeaturedCompany";

export const BaseCompanyList = ({
	children,
	styles,
	featuredCompanyIndex = 0,
	mainContainerSlot = null,
	companyListSlot = null,
}) => {
	const location = useLocation();
	const vertical = useSelector(getVertical);
	const defaultCount = useSelector(getNumberOfCompaniesToRenderCount);
	const companies = useSelector(sortedCompaniesSelector);
	const {
		count,
		partialQueryObjectForPaginationClicks,
		isInvalidQuery,
		page,
		redirectLocationSearch,
	} = returnPaginationInformation(location, defaultCount);

	const resolvedStyles =
		typeof styles === "function" ? styles(vertical) : styles;

	useEffect(() => {
		window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
	}, [page]);

	if (isInvalidQuery) {
		return <Navigate to={`${location.pathname}?${redirectLocationSearch}`} />;
	}

	if (companies.length === 0) {
		return <NoResults />;
	}

	return (
		<div className={resolvedStyles.mainContainer}>
			{mainContainerSlot}
			<div
				id="companyList"
				aria-label={`The Best ${transformHyphenatedString(vertical)} Companies of ${new Date().getFullYear()}`}
				className={resolvedStyles.companyListContainer}
			>
				{companyListSlot}
				<BaseCompanies
					companies={companies}
					sliceStart={(page - 1) * count}
					count={count}
				>
					{children}
				</BaseCompanies>
				{companies.length > count && (
					<Pagination
						page={page}
						pageCount={Math.ceil(companies.length / count)}
						count={count}
						partialQueryObjectForPaginationClicks={
							partialQueryObjectForPaginationClicks
						}
						className={resolvedStyles.paginationContainer}
						pageClassName={resolvedStyles.paginationListItem}
						previousClassName={resolvedStyles.previousListItem}
						nextClassName={resolvedStyles.nextListItem}
						pageLinkClassName={resolvedStyles.pageLink}
						previousLinkClassName={twMerge(
							`${resolvedStyles.pageLink} ${resolvedStyles.previousLink}`,
						)}
						nextLinkClassName={twMerge(
							`${resolvedStyles.pageLink} ${resolvedStyles.nextLink}`,
						)}
						activeLinkClassName={resolvedStyles.activeLink}
						disabledLinkClassName={resolvedStyles.disabledLink}
					/>
				)}
				<BaseFeaturedCompany company={companies[featuredCompanyIndex]}>
					{children}
				</BaseFeaturedCompany>
			</div>
		</div>
	);
};

BaseCompanyList.propTypes = {
	children: PropTypes.node,
	styles: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
	featuredCompanyIndex: PropTypes.number,
	mainContainerSlot: PropTypes.node,
	companyListSlot: PropTypes.node,
};

const transformHyphenatedString = (inputString) =>
	inputString
		.split("-")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
